import React, { useState, useEffect } from 'react';
import { Transaction } from '../../types';
import { getTrackingStatus } from '../../utils/timeCalculations';
import './TimeRemainingIndicator.css';

interface TimeRemainingIndicatorProps {
  transaction: Transaction;
}

/**
 * TimeRemainingIndicator component
 *
 * This component displays the remaining time for providing tracking information in a user-friendly format:
 * - For durations ≥ 1 day: Shows "X days Y hours" (e.g., "4 days 2 hours")
 * - For durations < 1 day but ≥ 1 hour: Shows hours and minutes (e.g., "5h 30m")
 * - For durations < 1 hour: Shows minutes only (e.g., "45m")
 *
 * The component also changes color based on the remaining time:
 * - Normal: > 12 hours remaining
 * - Warning: < 12 hours remaining
 * - Critical: < 4 hours remaining
 */
export const TimeRemainingIndicator: React.FC<TimeRemainingIndicatorProps> = ({ transaction }) => {
  const [trackingStatus, setTrackingStatus] = useState(getTrackingStatus(transaction));
  
  // Update the tracking status every minute
  useEffect(() => {
    const updateStatus = () => {
      setTrackingStatus(getTrackingStatus(transaction));
    };

    updateStatus();
    const interval = setInterval(updateStatus, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [transaction]);
  
  if (!trackingStatus.needsTracking) return null;
  
  return (
    <div className={`time-remaining-indicator ${trackingStatus.colorClass}`}>
      <div className="time-icon">⏱️</div>
      <div className="time-text">
        Time remaining: {trackingStatus.displayText}
      </div>
    </div>
  );
};

export default TimeRemainingIndicator;